/**
 * Series-formula bridge — Track 2 / C1.
 *
 * A `data-series` with `dataSource:"formula"` extracts its `[x, y][]`
 * points from a Lisp expression instead of a TypeScript `_adapter`
 * closure. This module owns the Lisp environment for that extraction
 * and the adapter factories that `graph-props.collectSeries` plugs into
 * `Graph.SeriesSpec.adapter` — so `DataIndex.extractPoints` stays
 * unchanged (it just sees an `adapter`).
 *
 * Scope `"row"` (`makeRowFormulaAdapter`): the formula is called once
 * per standard cell in the parent `data` row, with bindings:
 *   - `entry`  — the raw cell JSON (parsed Lisp value)
 *   - `date.*` — date helpers for the cell's column (UTC unless a tz
 *                is threaded in), via the shared `ModelLispLibrary`
 * X is the cell date's days/ms-since-epoch (`xSource`).
 *
 * Scope `"entry"` (`makeEntryFormulaAdapter`): the formula is called
 * once per raw `DateTime.Range` (custom/DTR) cell, with bindings:
 *   - `entry`     — the raw cell JSON
 *   - `dtr.start` / `dtr.end` — the DTR bounds as MSE (epoch ms)
 * X is `dtr.start` MSE. No `date.*` (no column) — referencing one throws.
 *
 * Both: the formula returns one Y (or a list of explicit `[x, y]`
 * points — fan-out). The `shape` selects how Y is typed: `"scalar"` (a
 * finite number), `"ohlc"` (a `hash-map` record `{open,high,low,close}`
 * for candles), or `"bar-width"` (a 2-list `[value, width]` for
 * `bar-chart-with-width`). `null` / a Y that doesn't match the shape
 * skips. Eval errors skip the point (per-point isolation, per
 * formula-data-series §9.2 Option A) — a bad cell never aborts the series.
 *
 * Deferred to later slices (see the graphing-demos remaining-work doc):
 *   - `rows["name"]` references + dependency-tracked invalidation
 *   - `scope:"sheet"` (group/bucket)
 *   - tz threading from the render context
 */

import type { Option } from "../../core/option.ts";
import type { DateTime, NaiveDate, TimezoneRegion, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";
import { Lisp } from "@tento-lisp/lisp";
import { ControlFlow } from "@tento-lisp/ops/control-flow";
import { StandardLibrary } from "@tento-lisp/ops/stdlib";

import {
  ModelLispLibrary,
  type ModelLispContext,
  type FormulaSheetReads,
} from "../../formula/model-formula.ts";
import type { BarWidthY, CellAdapter, GroupedCellAdapter, OhlcY, PointShape, PointY, RecordY } from "../../stores/graph-types.ts";
import { Json, jsonObjectOf } from "../../core/json.ts";

const ROW_LABEL_REGEX = /^rows\["([^"]+)"\]$/;
const ROW_ID_REGEX = /^r_([0-9a-f]{32})$/i;

/** No sheet is available during series extraction — `rows[…]` /
 * `cells "…"` references are deferred to a later slice, so the
 * library's sheet-backed helpers resolve against an empty view. */
const EMPTY_SHEET_VIEW: FormulaSheetReads = {
  peekCellsByLabelOrKey: () => null,
};

/**
 * Lisp environment for a `data-series` formula in `scope:"row"`.
 * Composes the shared `ModelLispLibrary` (date / cell helpers) and
 * the generic Lisp libraries, but binds `entry` for the current cell
 * and intentionally rejects row references — they need the sheet +
 * dependency tracking that this slice does not wire.
 */
export class DataSeriesLispEnvironment implements Lisp.Environment {
  readonly #ctx: Option<ModelLispContext>;
  readonly #scope: Map<string, Option<Lisp.Expression>>;
  constructor(
    scope: Map<string, Option<Lisp.Expression>>,
    ctx: Option<ModelLispContext>,
    public readonly evalCount: Lisp.EvalCount = { n: 0 },
  ) {
    this.#scope = scope;
    this.#ctx = ctx;
  }

  clone(keys: string[], values: Lisp.Expression[]): Lisp.Environment {
    const cloned = new Map(this.#scope);
    keys.forEach((key, index) => {
      const value = values[index];
      if (value !== undefined) cloned.set(key, value);
    });
    return new DataSeriesLispEnvironment(cloned, this.#ctx, this.evalCount);
  }

  set(key: string, value: Lisp.Expression): void {
    this.#scope.set(key, value);
  }

  get(key: string): Option<Lisp.Expression> {
    if (this.#scope.has(key)) return this.#scope.get(key) ?? null;

    if (ROW_LABEL_REGEX.test(key) || ROW_ID_REGEX.test(key)) {
      throw new Error(
        `data-series formula: row reference (${key}) is not supported in ` +
          `series-scoped formulas yet — coming in a later slice`,
      );
    }

    if (ModelLispLibrary.has(key)) {
      // `date.*` and other column-scoped helpers need a `ModelLispContext`.
      // `scope:"entry"` has no column (X comes from the DTR), so it runs
      // with a null context — surface a clear error rather than a deref.
      if (this.#ctx == null) {
        throw new Error(
          `data-series formula: ${key} needs a column and is not available ` +
            `in scope:"entry" (bind dtr.start / dtr.end instead)`,
        );
      }
      const op = nonnull(ModelLispLibrary.get(key), `data-series formula: ${key} is registered`);
      return op(this.#ctx);
    }
    if (ControlFlow.has(key)) return ControlFlow.get(key) ?? null;
    if (StandardLibrary.has(key)) return StandardLibrary.get(key) ?? null;

    // Unknown symbols are string literals — matches `ModelLispEnvironment`.
    return key;
  }
}

const finiteNum = (v: Option<Lisp.Expression>): v is number =>
  typeof v === "number" && Number.isFinite(v);

/**
 * Coerce a single formula Y to the typed `PointY` for `shape`, or `null`
 * if it does not match (the point is then skipped — parity with the
 * closures, which return `[]` on a malformed value):
 *   - `"scalar"`    — a finite number.
 *   - `"ohlc"`      — a record (from `hash-map`) with finite open/high/
 *                     low/close.
 *   - `"bar-width"` — a 2-list `[value, width]` of finite numbers.
 *   - `"record"`    — any plain object, passed through opaquely (the
 *                     renderer reads arbitrary fields; e.g. `canvas.funnel`
 *                     reads each conversion stage). A non-object skips.
 */
function coerceY(value: Lisp.Expression, shape: PointShape): Option<PointY> {
  if (shape === "record") {
    // The record's JSON fields (a `hash-map` is a plain object).
    const record: Option<RecordY> = jsonObjectOf(value);
    return record;
  }
  if (shape === "ohlc") {
    const rec = jsonObjectOf(value);
    const open = Json.number(rec, "open");
    const high = Json.number(rec, "high");
    const low = Json.number(rec, "low");
    const close = Json.number(rec, "close");
    if (open == null || high == null || low == null || close == null) return null;
    const ohlc: OhlcY = { open, high, low, close };
    return ohlc;
  }
  if (shape === "bar-width") {
    if (!Array.isArray(value) || value.length < 2) return null;
    const v = value[0];
    const w = value[1];
    if (!finiteNum(v) || !finiteNum(w)) return null;
    const barWidth: BarWidthY = [v, w];
    return barWidth;
  }
  return finiteNum(value) ? value : null;
}

/**
 * Coerce a list-returning formula into `[x, Y]` points (fan-out: one cell
 * → many points, e.g. intraday entries nested in a daily cell). Each
 * element must be an `[x, y]` pair with a finite X and a `Y` matching
 * `shape`; malformed pairs are skipped.
 */
function coercePoints(
  values: readonly Lisp.Expression[],
  shape: PointShape,
): [number, PointY][] {
  const out: [number, PointY][] = [];
  for (const el of values) {
    if (!Array.isArray(el) || el.length < 2) continue;
    const x = el[0];
    if (!finiteNum(x)) continue;
    const y = coerceY(el[1], shape);
    if (y != null) out.push([x, y]);
  }
  return out;
}

/**
 * Decide whether an array `result` is a fan-out (a list of `[x, Y]` pairs,
 * X from the formula) versus a single scalar Y that happens to be a list.
 * `"scalar"`/`"ohlc"`: any array is a fan-out (a scalar OHLC Y is an
 * object, not an array). `"bar-width"` is ambiguous — a scalar Y is itself
 * a 2-number list `[value, width]` — so a 2-list of two numbers is the
 * scalar; any other array (`[]`, or `[[x, [v, w]], …]`) is a fan-out.
 */
function isFanOut(result: readonly Lisp.Expression[], shape: PointShape): boolean {
  if (shape !== "bar-width") return true;
  const [first, second] = result;
  return !(result.length === 2 && finiteNum(first ?? null) && finiteNum(second ?? null));
}

/**
 * Reduce a formula `result` to `[x, Y][]`: a fan-out coerces each pair
 * (X from the formula); a scalar pairs the single coerced Y with `x`
 * (the cell-derived X). `null` / shape-mismatched Y → no points.
 */
function pointsFromResult(
  result: Option<Lisp.Expression>,
  shape: PointShape,
  x: number,
): [number, PointY][] {
  if (result == null) return [];
  if (Array.isArray(result) && isFanOut(result, shape)) {
    return coercePoints(result, shape);
  }
  const y = coerceY(result, shape);
  return y == null ? [] : [[x, y]];
}

/** Which X the scalar branch derives from the cell's date. */
export type XSource = "date.dse" | "date.mse";

/**
 * Build a `CellAdapter` that evaluates `formula` per standard cell in
 * `scope:"row"`. The AST is parsed once; each cell gets a fresh
 * environment with its own `entry` binding. The formula returns either
 * a scalar Y (typed by `shape`; X at the cell's date — from `xSource`)
 * or a list of explicit `[x, Y]` points (fan-out; X comes from the
 * formula). Custom (DTR) cells are skipped — those belong to
 * `scope:"entry"`.
 */
export function makeRowFormulaAdapter(
  formula: string,
  tz: Option<TimezoneRegion>,
  xSource: XSource,
  shape: PointShape,
): CellAdapter<PointY> {
  const ast = Lisp.unsafeParse(formula);
  return (d: Option<NaiveDate.Partial>, _dtr, json): [number, PointY][] => {
    if (!d) return [];
    const ctx: ModelLispContext = {
      sheet: EMPTY_SHEET_VIEW,
      column: d,
      style: new Map<string, string>(),
      tz,
      dbg: null,
    };
    const scope = new Map<string, Lisp.Expression>([["entry", json]]);
    const env = new DataSeriesLispEnvironment(scope, ctx);
    let result: Option<Lisp.Expression>;
    try {
      result = Lisp.evalThrows(ast, env) ?? null;
    } catch (err) {
      Tracing.log("data-series-formula/row-skipped", {
        date: d.start.toString(),
        error: err instanceof Error ? err.message : String(err),
      });
      return [];
    }
    const x = xSource === "date.mse" ? d.start.mse : d.start.dse;
    return pointsFromResult(result, shape, x);
  };
}

/**
 * Build a `CellAdapter` for `scope:"entry"` — the formula is evaluated
 * once per raw `DateTime.Range` (custom/DTR) cell, replacing the builtin
 * `adapter(null, dtr, json)` path. Bindings: `entry` (the cell JSON),
 * `dtr.start` / `dtr.end` (the DTR bounds as MSE). X is `dtr.start` MSE;
 * the formula returns one Y (typed by `shape`) or a list of explicit
 * `[x, Y]` points (fan-out). Standard (column) cells are skipped — those
 * belong to `scope:"row"`. There is no column, so `date.*` is unavailable
 * (the env throws on it); the DTR bounds are the time reference instead.
 */
export function makeEntryFormulaAdapter(
  formula: string,
  _tz: Option<TimezoneRegion>,
  shape: PointShape,
): CellAdapter<PointY> {
  const ast = Lisp.unsafeParse(formula);
  return (_d, dtr: Option<DateTime.Range<Utc>>, json): [number, PointY][] => {
    if (!dtr) return [];
    const startMse = dtr.start.mse;
    const scope = new Map<string, Lisp.Expression>([
      ["entry", json],
      ["dtr.start", startMse],
      ["dtr.end", dtr.end.mse],
    ]);
    const env = new DataSeriesLispEnvironment(scope, null);
    let result: Option<Lisp.Expression>;
    try {
      result = Lisp.evalThrows(ast, env) ?? null;
    } catch (err) {
      Tracing.log("data-series-formula/entry-skipped", {
        startMse,
        error: err instanceof Error ? err.message : String(err),
      });
      return [];
    }
    return pointsFromResult(result, shape, startMse);
  };
}

/**
 * Build a `GroupedCellAdapter` for `scope:"sheet"` — the formula is
 * evaluated ONCE over all cells in the range (replacing a `_groupAdapter`).
 * Bindings:
 *   - `data`  — a list of `[x, entry]` pairs (x = MSE), sorted ascending by
 *               x, for every standard + DTR cell in range
 *   - `count` — `data.length`
 * The formula returns the full `[[x, y], …]` point set (X from the formula,
 * Y typed by `shape`); cross-cell aggregation uses `group-by`. There is no
 * single column, so `date.*` is unavailable (the env throws on it) — the
 * MSE X in `data` is the time reference. Malformed pairs are skipped; an
 * eval error drops the whole series (sheet scope is one call, per
 * formula-data-series §9.2) rather than one point.
 */
export function makeSheetFormulaAdapter(
  formula: string,
  _tz: Option<TimezoneRegion>,
  shape: PointShape,
): GroupedCellAdapter<PointY> {
  const ast = Lisp.unsafeParse(formula);
  return (data, dataCustom): [number, PointY][] => {
    const pairs: [number, Lisp.Expression][] = [];
    for (const [date, json] of data) pairs.push([date.start.mse, json]);
    for (const [dtr, json] of dataCustom) pairs.push([dtr.start.mse, json]);
    pairs.sort((a, b) => a[0] - b[0]);
    const dataLisp: Lisp.Expression[] = pairs.map(([x, json]) => [x, json]);
    const scope = new Map<string, Lisp.Expression>([
      ["data", dataLisp],
      ["count", pairs.length],
    ]);
    const env = new DataSeriesLispEnvironment(scope, null);
    let result: Option<Lisp.Expression>;
    try {
      result = Lisp.evalThrows(ast, env) ?? null;
    } catch (err) {
      Tracing.log("data-series-formula/sheet-aggregate-skipped", {
        error: err instanceof Error ? err.message : String(err),
      });
      return [];
    }
    if (result == null || !Array.isArray(result)) return [];
    return coercePoints(result, shape);
  };
}
